import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiUsers,
  FiVideo,
} from 'react-icons/fi';

import Logo from '../components/common/Logo';
import StatusBadge, { STATUS_META } from '../components/admin/StatusBadge';
import { useAuth } from '../context/AuthContext';
import {
  getClinicianAppointments,
  getClinicianMe,
  getClinicianSummary,
  updateClinicianAppointmentStatus,
} from '../api/client';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelative,
  formatTime,
  initials,
  titleCase,
} from '../utils/format';

const MODALITY_ICON = { video: FiVideo, 'in-person': FiMapPin, phone: FiPhone };

const SCOPES = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'all', label: 'All' },
];

function NextSession({ appointment }) {
  if (!appointment) {
    return (
      <div className="mm-next mm-next--empty">
        <FiCalendar size={20} aria-hidden="true" />
        <div>
          <strong>No upcoming sessions</strong>
          <span>Your next booking will appear here.</span>
        </div>
      </div>
    );
  }

  const ModalityIcon = MODALITY_ICON[appointment.modality] ?? FiVideo;

  return (
    <div className="mm-next">
      <span className="mm-next__label">Next session</span>
      <div className="mm-next__row">
        <span className="mm-person__avatar" aria-hidden="true">
          {initials(appointment.customer.name)}
        </span>
        <div className="mm-next__body">
          <strong>{appointment.customer.name}</strong>
          <span>
            {formatDateTime(appointment.scheduledFor)} &middot;{' '}
            {formatRelative(appointment.scheduledFor)}
          </span>
        </div>
        <span className="mm-modality mm-next__modality">
          <ModalityIcon size={14} aria-hidden="true" />
          {titleCase(appointment.modality)}
        </span>
      </div>
    </div>
  );
}

function AppointmentCard({ appointment, onStatus, busy }) {
  const a = appointment;
  const ModalityIcon = MODALITY_ICON[a.modality] ?? FiVideo;
  const isFuture = new Date(a.scheduledFor) > new Date();

  return (
    <article className="mm-appt">
      <div className="mm-appt__when">
        <span className="mm-appt__day">{formatDate(a.scheduledFor, { withYear: false })}</span>
        <span className="mm-appt__time">{formatTime(a.scheduledFor)}</span>
        <span className="mm-appt__rel">{formatRelative(a.scheduledFor)}</span>
      </div>

      <div className="mm-appt__main">
        <div className="mm-person">
          <span className="mm-person__avatar" aria-hidden="true">
            {initials(a.customer.name)}
          </span>
          <span className="mm-person__text">
            <span className="mm-person__name">{a.customer.name}</span>
            <span className="mm-person__sub">{a.customer.email}</span>
          </span>
        </div>

        <div className="mm-appt__meta">
          <span className="mm-modality">
            <ModalityIcon size={13} aria-hidden="true" />
            {titleCase(a.modality)}
          </span>
          <span>&middot;</span>
          <span>{a.sessionType}</span>
          <span>&middot;</span>
          <span>{a.durationMinutes} min</span>
          <span>&middot;</span>
          <span>{formatCurrency(a.fee, a.currency)}</span>
        </div>

        {a.notes && <p className="mm-appt__notes">{a.notes}</p>}
        {a.location && <p className="mm-appt__notes">{a.location}</p>}
      </div>

      <div className="mm-appt__side">
        <StatusBadge status={a.status} />

        {/* Outcomes only make sense once the session has happened. */}
        {!isFuture && a.status === 'scheduled' && (
          <div className="mm-appt__actions">
            {['completed', 'no-show'].map((status) => (
              <Button
                key={status}
                variant="outline-ink"
                size="sm"
                disabled={busy}
                onClick={() => onStatus(a.id, status)}
              >
                {busy ? (
                  <Spinner animation="border" size="sm" aria-hidden="true" />
                ) : (
                  STATUS_META[status].label
                )}
              </Button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export default function ClinicianPortal() {
  const { user, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [nextSession, setNextSession] = useState(null);
  const [scope, setScope] = useState('upcoming');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadSummary = useCallback(async () => {
    const result = await getClinicianSummary();
    setSummary(result.summary);
    setNextSession(result.nextSession);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meResult] = await Promise.all([getClinicianMe(), loadSummary()]);
        if (!cancelled) setProfile(meResult.profile);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSummary]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await getClinicianAppointments({
          scope,
          q: search,
          pageSize: 20,
        });
        if (cancelled) return;
        setRows(result.appointments);
        setPagination(result.pagination);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load your schedule.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scope, search]);

  const handleStatus = async (id, status) => {
    setBusyId(id);
    setError(null);
    try {
      const result = await updateClinicianAppointmentStatus(id, status);
      setRows((prev) => prev.map((r) => (r.id === id ? result.appointment : r)));
      await loadSummary();
    } catch (err) {
      setError(err.body?.errors?.status || err.message || 'Could not update that session.');
    } finally {
      setBusyId(null);
    }
  };

  const s = summary || {};

  return (
    <div className="mm-admin">
      <header className="mm-admin__bar">
        <Container className="mm-admin__bar-inner">
          <Link to="/" className="mm-admin__brand" aria-label="MindlyFriend home">
            <Logo markSize={32} />
            <span className="mm-admin__tag">Clinician</span>
          </Link>

          <div className="mm-admin__bar-right">
            <span className="mm-admin__user">
              Signed in as <strong>{user?.email}</strong>
            </span>
            <Link to="/" className="mm-auth__back">
              <FiArrowLeft size={15} aria-hidden="true" />
              Back to site
            </Link>
            <Button variant="outline-ink" size="sm" onClick={logout}>
              Log out
            </Button>
          </div>
        </Container>
      </header>

      <Container className="mm-admin__body">
        <div className="mm-portal__head">
          {profile && (
            <img
              className="mm-portal__photo"
              src={profile.image}
              alt=""
              width={56}
              height={56}
            />
          )}
          <div>
            <h1 className="mm-h2 mb-1">
              {profile ? profile.name : user?.name}
            </h1>
            <p className="mm-lead mb-0">
              {profile
                ? `${profile.title} · ${profile.location}`
                : 'Your MindlyFriend schedule'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <NextSession appointment={nextSession} />

        <Row className="g-3 mb-4">
          {[
            { Icon: FiClock, label: 'Upcoming', value: s.upcoming ?? 0, tone: 'amber' },
            { Icon: FiCheckCircle, label: 'Completed', value: s.completed ?? 0, tone: 'green' },
            { Icon: FiUsers, label: 'Clients', value: s.clients ?? 0, tone: 'blue' },
            {
              Icon: FiCalendar,
              label: 'Earned',
              value: formatCurrency(s.revenue, s.currency, { compact: true }),
              tone: 'teal',
            },
          ].map(({ Icon, label, value, tone }) => (
            <Col key={label} xs={6} lg={3}>
              <div className={`mm-stat mm-stat--${tone}`}>
                <span className="mm-stat__icon" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <div className="mm-stat__body">
                  <div className="mm-stat__label">{label}</div>
                  <div className="mm-stat__value">{value}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="mm-portal__toolbar">
          <div className="mm-portal__tabs" role="tablist" aria-label="Filter sessions">
            {SCOPES.map((option) => (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={scope === option.key}
                className={`mm-portal__tab ${
                  scope === option.key ? 'mm-portal__tab--active' : ''
                }`}
                onClick={() => setScope(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mm-filters__search mm-portal__search">
            <FiSearch size={16} aria-hidden="true" />
            <label className="mm-sr-only" htmlFor="portal-search">
              Search your clients
            </label>
            <input
              id="portal-search"
              type="search"
              placeholder="Search client or reference…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="mm-portal__list">
          {loading &&
            Array.from({ length: 4 }, (_, i) => (
              <div className="mm-skeleton" key={`sk-${i}`} style={{ height: 96 }}>
                <div className="mm-skeleton__bar" style={{ width: '40%' }} />
                <div className="mm-skeleton__bar" style={{ width: '65%' }} />
              </div>
            ))}

          {!loading && rows.length === 0 && (
            <div className="mm-portal__empty">
              <strong>Nothing here yet.</strong>
              <span>
                {scope === 'upcoming'
                  ? 'You have no upcoming sessions booked.'
                  : 'No sessions match this filter.'}
              </span>
            </div>
          )}

          {!loading &&
            rows.map((a) => (
              <AppointmentCard
                key={a.id}
                appointment={a}
                onStatus={handleStatus}
                busy={busyId === a.id}
              />
            ))}
        </div>

        {!loading && pagination.total > rows.length && (
          <p className="mm-faint mt-3" style={{ fontSize: '0.85rem' }}>
            Showing {rows.length} of {pagination.total}.
          </p>
        )}
      </Container>
    </div>
  );
}
